import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories } from '@/mocks/categories';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/' || location.pathname === '/en' || location.pathname === '/pt-br' || location.pathname === '/de';
  const isTransparent = transparent && !scrolled && isHome;

  const bgClass = isTransparent
    ? 'bg-transparent'
    : 'bg-background-50 border-b border-background-300 shadow-sm';

  const textClass = isTransparent ? 'text-background-50' : 'text-foreground-900';

  const navLinks = [
    { label: t('nav_home'), href: '/' },
    { label: t('nav_shopping_guide'), href: '/guides' },
    { label: t('nav_reviews'), href: '/reviews' },
    { label: t('nav_shipping'), href: '/shipping' },
    { label: t('nav_qc'), href: '/qc' },
    { label: t('nav_safety'), href: '/safety' },
    { label: t('nav_faq'), href: '/faq' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}>
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className={`font-heading text-lg md:text-xl font-semibold tracking-tight ${textClass}`}>
            CSSBuy<span className="text-primary-500">BR</span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href + link.label}
                href={link.href}
                className={`px-3 py-2 text-sm font-semibold rounded-md hover:bg-background-100/80 transition-colors ${textClass}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <div className="hidden md:block">
              <LanguageSwitcher transparent={isTransparent} />
            </div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center px-4 py-2 text-sm font-medium rounded-md btn-primary"
            >
              {t('cta_main_site')}
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-md ${textClass}`}
              aria-label="Toggle menu"
            >
              <i className={mobileMenuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
          {navLinks.map((link) => (
            <a
              key={link.href + link.label}
              href={link.href}
              className="block px-3 py-2 text-sm font-medium text-foreground-800 rounded-md hover:bg-background-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}

          {/* Mobile Categories */}
          <div className="pt-2 border-t border-background-200/70">
            <button
              onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
              className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-foreground-800 rounded-md hover:bg-background-100"
            >
              <span className="flex items-center gap-2">
                <i className="ri-apps-line text-primary-500"></i>
                {t('nav_categories')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCategoriesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCategoriesOpen && (
              <div className="grid grid-cols-2 gap-1 mt-1 mb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`${cat.icon} text-primary-400 text-sm flex-shrink-0`}></i>
                    <span className="truncate">{t(cat.nameKey)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-background-200/70">
            <LanguageSwitcher />
          </div>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block mt-2 px-4 py-2 text-sm font-medium text-center rounded-md btn-primary"
          >
            {t('cta_main_site')}
          </a>
        </div>
      )}
    </header>
  );
}